import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  BudgetFormHeader,
  BasicInfoSection,
  BudgetItemsSection,
  BudgetSummarySection,
  BudgetFormActions,
} from "@/components/budgets/form";

import { useBudgetForm } from "@/hooks/budgets/useBudgetForm";
import { useBudgetMutations } from "@/hooks/budgets/useBudgetMutations";
import { useClients } from "@/hooks/useClients";
import { useCategories } from "@/hooks/useCategories";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useServices } from "@/hooks/useServices";
import { useProducts } from "@/hooks/useProducts";
import { useUsers } from "@/hooks/useUsers";
import {
  Budget,
  BudgetFormData,
  BudgetValidationResult,
  BudgetStatus
} from "@/types/budget";

// All data now loaded from Supabase via hooks

interface AutoSaveStatus {
  isSaving: boolean;
  lastSaved?: Date;
  status: "idle" | "saving" | "saved" | "error";
}

export default function BudgetFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // State
  const [budget, setBudget] = useState<Budget>();
  const [isLoading, setIsLoading] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>({
    isSaving: false,
    status: "idle"
  });
  const [validation, setValidation] = useState<BudgetValidationResult>();

  // Hooks
  const { clients } = useClients();
  const { categories } = useCategories({ type: "income" });
  const { costCenters } = useCostCenters();
  const { services } = useServices();
  const { products } = useProducts();
  const { users } = useUsers();

  const { create, update, duplicate, isCreating, isUpdating } = useBudgetMutations();

  // Form hook
  const form = useBudgetForm({
    defaultValues: isEditing ? undefined : {
      validity_type: "days",
      validity_days: 7,
      priority: "medium",
      payment_terms: "30 dias",
      items: []
    }
  });

  const {
    form: formInstance,
    values,
    isValid,
    isDirty,
    isSubmitting,
    handleSubmit,
    reset,
    validate
  } = form;

  // Load budget data for editing
  useEffect(() => {
    if (isEditing && id) {
      loadBudget(id);
    }
  }, [id, isEditing]);

  const loadBudget = async (budgetId: string) => {
    setIsLoading(true);
    try {
      // Substituir com chamada real à API
      // const budgetData = await getBudgetById(budgetId);

      // Mock data para teste
      const mockBudget: Budget = {
        id: budgetId,
        company_id: "1",
        user_id: "1",
        number: "BUD-2024-001",
        client_id: "1",
        title: "Orçamento de Desenvolvimento Web",
        description: "Desenvolvimento de website institucional",
        total_value: 10000.00,
        subtotal: 10000.00,
        discount_amount: 0,
        discount_percentage: 0,
        tax_amount: 0,
        final_value: 10000.00,
        status: "draft" as BudgetStatus,
        priority: "medium",
        validity_type: "days",
        validity_days: 7,
        notes: "Orçamento para desenvolvimento de site",
        terms: "Termos e condições padrão",
        payment_terms: "50% à vista, 50% na entrega",
        delivery_terms: "30 dias úteis",
        metadata: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        client_name: "Empresa ABC Ltda",
        items_count: 3,
      };

      setBudget(mockBudget);

      // Populate form with budget data
      const formData: BudgetFormData = {
        client_id: mockBudget.client_id,
        title: mockBudget.title,
        description: mockBudget.description,
        items: [], // Load items separately
        discount_percentage: mockBudget.discount_percentage,
        discount_amount: mockBudget.discount_amount,
        priority: mockBudget.priority,
        validity_type: mockBudget.validity_type,
        validity_days: mockBudget.validity_days,
        validity_date: mockBudget.validity_date,
        notes: mockBudget.notes,
        terms: mockBudget.terms,
        payment_terms: mockBudget.payment_terms,
        delivery_terms: mockBudget.delivery_terms,
      };

      reset(formData);

    } catch (error) {
      console.error("Error loading budget:", error);
      toast.error("Erro ao carregar orçamento");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-save functionality
  useEffect(() => {
    if (!isDirty || isEditing || !isValid) return;

    const timer = setTimeout(() => {
      autoSave();
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [values, isDirty, isValid, isEditing]);

  const autoSave = useCallback(async () => {
    if (!isValid) return;

    setAutoSaveStatus({ isSaving: true, status: "saving" });

    try {
      // Substituir com chamada real à API de auto-save
      // await saveDraft(values);

      setAutoSaveStatus({
        isSaving: false,
        lastSaved: new Date(),
        status: "saved"
      });
    } catch (error) {
      console.error("Auto-save error:", error);
      setAutoSaveStatus({
        isSaving: false,
        status: "error"
      });
    }
  }, [values, isValid]);

  // Form submission
  const onSubmit = async (data: BudgetFormData) => {
    try {
      if (isEditing && budget) {
        const updatedBudget = await update(budget.id, data);
        toast.success("Orçamento atualizado com sucesso!");
        setBudget(updatedBudget);
      } else {
        const newBudget = await create(data);
        toast.success("Orçamento criado com sucesso!");
        navigate(`/services/budgets/${newBudget.id}/edit`);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Erro ao salvar orçamento");
    }
  };

  // Action handlers
  const handleSaveAndSend = async () => {
    const validationResult = validate();
    setValidation(validationResult);

    if (!validationResult.isValid) {
      toast.error("Corrija os erros antes de enviar");
      return;
    }

    // Implement save and send logic
    toast.success("Orçamento salvo e enviado!");
  };

  const handlePreview = () => {
    if (!budget) {
      toast.error("Salve o orçamento antes de visualizar");
      return;
    }
    // Navigate to preview page
    navigate(`/services/budgets/${budget.id}/preview`);
  };

  const handleDuplicate = async () => {
    if (!budget) return;

    try {
      const duplicatedBudget = await duplicate(budget.id);
      toast.success("Orçamento duplicado com sucesso!");
      navigate(`/services/budgets/${duplicatedBudget.id}/edit`);
    } catch (error) {
      console.error("Duplicate error:", error);
      toast.error("Erro ao duplicar orçamento");
    }
  };

  const handleSaveAsTemplate = () => {
    // Implement save as template logic
    toast.success("Modelo salvo com sucesso!");
  };

  const handleReset = () => {
    if (window.confirm("Tem certeza que deseja redefinir o formulário?")) {
      reset();
      setValidation(undefined);
      toast.info("Formulário redefinido");
    }
  };

  // Validate on form changes
  useEffect(() => {
    const result = validate();
    setValidation(result);
  }, [values, validate]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-64 mb-2"></div>
          <div className="h-4 bg-muted rounded w-96"></div>
        </div>
        <div className="grid gap-6">
          <div className="h-64 bg-muted rounded-lg"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <Form {...formInstance}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <BudgetFormHeader
          budget={budget}
          isEditing={isEditing}
          isSubmitting={isSubmitting}
          onSave={handleSubmit(onSubmit)}
          onSaveAndSend={handleSaveAndSend}
          onPreview={handlePreview}
          onDuplicate={handleDuplicate}
          onSaveAsTemplate={handleSaveAsTemplate}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <BasicInfoSection
              clients={clients || []}
              users={users || []}
            />

            {/* Items Section */}
            <BudgetItemsSection
              services={services || []}
              products={products || []}
              categories={categories || []}
              costCenters={costCenters || []}
            />

            {/* Summary Section */}
            <BudgetSummarySection
              taxRate={0} // Configurable tax rate
              currency="BRL"
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <BudgetFormActions
              isEditing={isEditing}
              isSubmitting={isSubmitting}
              onSave={handleSubmit(onSubmit)}
              onSaveAndSend={handleSaveAndSend}
              onPreview={handlePreview}
              onDuplicate={handleDuplicate}
              onSaveAsTemplate={handleSaveAsTemplate}
              onReset={handleReset}
              validation={validation}
              autoSaveStatus={autoSaveStatus}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}